import type { AudioFeatures, FeatureProvider } from "../audio/types";
import { ZERO_FEATURES } from "../audio/types";
import { filterBySceneProfile } from "../ui/advancedControls";
import type { CameraControlMode, CameraSyncState, OutputProjection, Renderer } from "../viz/renderer";
import type { BioclastParams, QualityTier } from "../viz/scenes/bioclast";
import { applyScenePreset } from "../viz/scenes/applyScenePreset";
import {
  SCENES,
  SCENE_MOOD_ORDER,
  findSceneById,
  findVariantById,
  getSceneId,
  getVariantId,
  parseRemoteSceneScope,
  parseRemoteRuntimeTarget,
  sceneIsAvailableForRemoteTarget,
  sceneIsAvailableInRemoteScope,
  type RemoteSceneScope,
  type RemoteRuntimeTarget,
  type ScenePreset,
  type SceneVariant,
} from "../viz/scenes/scenePresets";

const PROTOCOL_VERSION = 2;
const QUALITY_TIERS = new Set<QualityTier>(["mobile", "standard", "high", "ultra", "lab"]);
const VISUAL_PARAM_SPECS = {
  intensity: { min: 0, max: 4 },
  starSize: { min: 0.05, max: 1 },
  opacity: { min: 0, max: 1 },
  bassGravity: { min: 0, max: 4 },
  midCurl: { min: 0, max: 4 },
  trebleShimmer: { min: 0, max: 4 },
  cymaticStrength: { min: 0, max: 4 },
  flowSpeed: { min: 0, max: 4 },
  fieldTightness: { min: 0.4, max: 1.6 },
  atmosphere: { min: 0, max: 1.5 },
  backdropBlur: { min: 0, max: 120 },
  cameraZoom: { min: 0.15, max: 3.5 },
  cameraTilt: { min: 0, max: 1 },
  cameraSpin: { min: 0, max: 3 },
  camAutoRotateH: { min: -1, max: 1 },
  camAutoRotateV: { min: -1, max: 1 },
  flythroughSpeed: { min: 0.1, max: 3 },
  beatDolly: { min: 0, max: 1 },
  paletteHue: { min: 0, max: 360 },
  saturation: { min: 0, max: 1 },
  bloomEnabled: { min: 0, max: 1, boolean: true },
  bloomStrength: { min: 0, max: 3 },
  bloomRadius: { min: 0, max: 1.2 },
  bloomThreshold: { min: 0, max: 1 },
  feedbackStrength: { min: 0, max: 0.95 },
  feedbackDecay: { min: 0.8, max: 1 },
  feedbackZoom: { min: -0.05, max: 0.05 },
  feedbackRotate: { min: -0.05, max: 0.05 },
  feedbackDriftX: { min: -0.02, max: 0.02 },
  feedbackDriftY: { min: -0.02, max: 0.02 },
  mathPointSize: { min: 0.25, max: 2 },
  mathAmplitude: { min: 0, max: 2 },
  mathFrequency: { min: 0.2, max: 4 },
  mathRotationSpeed: { min: 0, max: 2 },
  mathDepthScale: { min: 0, max: 1.4 },
  mathWarmth: { min: 0, max: 1 },
  mathColorSpread: { min: 0, max: 1 },
  mathColorFlow: { min: 0, max: 2 },
  energyOrbSize: { min: 0.1, max: 0.8 },
  energyOrbDensity: { min: 0.1, max: 3 },
  energyOrbSpeed: { min: 0.05, max: 3 },
  energyOrbAtmosphereGlow: { min: 0, max: 5 },
  energyOrbFractalScale: { min: 0.3, max: 1.5 },
  energyOrbFractalIters: { min: 2, max: 8 },
  energyOrbSmoothness: { min: 0, max: 0.15 },
  energyOrbAsymmetry: { min: 0, max: 1 },
  sphereDetail: { min: 32, max: 192 },
  sphereSize: { min: 0.6, max: 3 },
  sphereDisplacement: { min: 0, max: 1.2 },
  sphereVertexAudioMix: { min: 0, max: 1.5 },
  sphereNoiseScale: { min: 0.4, max: 6 },
  sphereNoiseIntensity: { min: 0, max: 1.5 },
  sphereNoiseSpeed: { min: 0, max: 2 },
  sphereWaveSpeed: { min: 0, max: 2 },
  sphereColorIntensity: { min: 0, max: 4 },
  sphereRotationSpeed: { min: 0, max: 2 },
  sphereSolidInterior: { min: 0, max: 1, boolean: true },
  meshGridWidth: { min: 20, max: 200 },
  meshGridDepth: { min: 20, max: 200 },
  meshCellSize: { min: 0.05, max: 0.5 },
  meshWaveHeight: { min: 0, max: 3 },
  meshWaveDepth: { min: 0, max: 2 },
  meshComplexity: { min: 0, max: 1 },
  meshHarmonics: { min: 0, max: 1 },
  meshBeatSharpness: { min: 0, max: 1 },
} as const satisfies Partial<Record<keyof BioclastParams, {
  min: number;
  max: number;
  boolean?: boolean;
}>>;
type VisualParamKey = keyof typeof VISUAL_PARAM_SPECS;

type RemoteRuntimeMessage = {
  type?: string;
  command?: string;
  value?: unknown;
  features?: unknown;
  nonce?: string;
  seq?: number;
};

type Selection = {
  scene: ScenePreset;
  variant: SceneVariant | null;
  selectionId: string;
};

type RemoteParamSchema = {
  id: string;
  label: string;
  min?: number;
  max?: number;
  default: number | boolean;
  step?: number;
  type: "float" | "toggle";
};

type OutputSettings = {
  brightness: number;
  width: number;
  height: number;
  detail: "auto" | "light" | "standard" | "dense";
  projection: OutputProjection;
  warp: number;
  fov: number;
  calibration: boolean;
  tileX: number;
  tileY: number;
  tileColumns: number;
  tileRows: number;
  viewX: number;
  viewY: number;
  viewWidth: number;
  viewHeight: number;
  fill: number;
  fillY: number;
};

const OUTPUT_PROJECTIONS = new Set<OutputProjection>(["flat", "curved", "cylinder", "sphere"]);

function clamp01(value: unknown): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;
  return Math.max(0, Math.min(1, numberValue));
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function optionalFloatArray(value: unknown): Float32Array | undefined {
  if (value instanceof Float32Array) return value;
  if (!Array.isArray(value)) return undefined;
  return Float32Array.from(value, (item) => Number(item) || 0);
}

function safeOrigin(value: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function websocketUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(
      /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    parsed.protocol = parsed.protocol === "http:" ? "ws:" : "wss:";
    parsed.pathname = "/ws";
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return null;
  }
}

function selectionForId(id: string | null): Selection | null {
  const scene = findSceneById(id);
  if (scene) return { scene, variant: null, selectionId: getSceneId(scene) };
  if (!id) return null;
  for (const candidate of SCENES) {
    const variant = findVariantById(candidate, id);
    if (variant) return { scene: candidate, variant, selectionId: id };
  }
  return null;
}

function selectionItems(
  scope: RemoteSceneScope,
  target: RemoteRuntimeTarget,
): Array<{ id: string; label: string; group: string }> {
  const moodOrder = new Map<string, number>(
    [...SCENE_MOOD_ORDER, "All"].map((mood, index) => [mood, index]),
  );
  return SCENES.filter((scene) => (
    sceneIsAvailableInRemoteScope(scene, scope) &&
    sceneIsAvailableForRemoteTarget(scene, target)
  )).sort((left, right) => (
    (moodOrder.get(left.mood ?? "All") ?? 999) -
    (moodOrder.get(right.mood ?? "All") ?? 999)
  )).flatMap((scene) => {
    const sceneId = getSceneId(scene);
    const group = scene.mood ?? "All";
    const base = [{ id: sceneId, label: scene.name, group }];
    const variants = (scene.variants ?? []).map((variant) => ({
      id: getVariantId(scene, variant),
      label: `${scene.name} - ${variant.name}`,
      group,
    }));
    return [...base, ...variants];
  });
}

export class RemoteRuntimeBridge implements FeatureProvider {
  private features: AudioFeatures = { ...ZERO_FEATURES };
  private beatUpdatedAt = performance.now();
  private current: Selection | null = null;
  private websocket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private stopped = false;
  private frozen = false;
  private inputBeatActive = false;
  private readonly visualOverrides = new Map<VisualParamKey, number | boolean>();
  private readonly sceneOverrides = new Map<string, Map<VisualParamKey, number | boolean>>();
  private output: OutputSettings = {
    brightness: 1,
    width: 2048,
    height: 1024,
    detail: "auto",
    projection: "flat",
    warp: 0.65,
    fov: 0.65,
    calibration: false,
    tileX: 0,
    tileY: 0,
    tileColumns: 1,
    tileRows: 1,
    viewX: 0,
    viewY: 0,
    viewWidth: 1,
    viewHeight: 1,
    fill: 1,
    fillY: 1,
  };
  private readonly scope: RemoteSceneScope;
  private buildId = document.documentElement.dataset.pfxBuildId ?? "dev";
  private readonly parentOrigin: string | null;
  private readonly nonce: string | null;
  private readonly target: RemoteRuntimeTarget;
  private readonly onMessage = (event: MessageEvent): void => this.handleParentMessage(event);

  constructor(
    private readonly renderer: Renderer,
    private readonly canvas: HTMLCanvasElement,
    params: URLSearchParams,
  ) {
    this.parentOrigin = safeOrigin(params.get("parentOrigin"));
    this.nonce = params.get("bridgeNonce");
    this.target = parseRemoteRuntimeTarget(params.get("target"));
    this.scope = parseRemoteSceneScope(params.get("scope"));
    const syncEpochValue = params.get("outputSyncEpoch");
    const syncEpoch = syncEpochValue === null || syncEpochValue.trim() === ""
      ? null
      : Number(syncEpochValue);
    this.renderer.setVisualSyncEpoch(syncEpoch !== null && Number.isFinite(syncEpoch) ? syncEpoch : null);
    window.addEventListener("message", this.onMessage);
    this.renderer.onCameraChange = (camera): void => {
      this.post({ type: "pfx:cameraChanged", camera });
    };
    this.setOutput({
      brightness: params.get("outputBrightness"),
      width: params.get("outputWidth"),
      height: params.get("outputHeight"),
      detail: params.get("outputDetail"),
      projection: params.get("outputProjection"),
      warp: params.get("outputWarp"),
      fov: params.get("outputFov"),
      calibration: params.get("outputCalibration"),
      tileX: params.get("outputTileX"),
      tileY: params.get("outputTileY"),
      tileColumns: params.get("outputTileColumns"),
      tileRows: params.get("outputTileRows"),
      viewX: params.get("outputViewX"),
      viewY: params.get("outputViewY"),
      viewWidth: params.get("outputViewWidth"),
      viewHeight: params.get("outputViewHeight"),
      fill: params.get("outputFill"),
      fillY: params.get("outputFillY"),
    });
    for (const key of Object.keys(VISUAL_PARAM_SPECS) as VisualParamKey[]) {
      const value = params.get(`visual_${key}`);
      if (value !== null) this.setVisualParam(key, value, null);
    }
    const cameraMode = params.get("cameraMode");
    if (cameraMode === "auto" || cameraMode === "free" || cameraMode === "fly") {
      this.renderer.setCameraControlMode(cameraMode);
    }

    if (this.target === "sl-moap") {
      this.renderer.params.qualityTier = this.wallQualityTier();
      this.renderer.setPixelRatioCap(1);
    }
    else if (this.target === "obs-wall") this.renderer.params.qualityTier = "standard";

    const requested = params.get("variantId") || params.get("sceneId") || "plasma-sheet";
    this.applySelection(requested, null);
    for (const key of Object.keys(VISUAL_PARAM_SPECS) as VisualParamKey[]) {
      const value = params.get(`param_${key}`);
      if (value !== null) this.setSceneParam(key, value, null, requested);
    }

    const wsHost = params.get("wsHost");
    if (wsHost) this.connectWebsocket(wsHost);

    queueMicrotask(() => void this.announceReady());
  }

  private async announceReady(): Promise<void> {
    try {
      const catalogUrl = new URL("/scene-catalog.v1.json", window.location.origin);
      catalogUrl.searchParams.set("remoteRuntime", String(Date.now()));
      const response = await fetch(catalogUrl, { cache: "no-store" });
      if (response.ok) {
        const catalog = await response.json() as { buildId?: unknown };
        if (typeof catalog.buildId === "string" && catalog.buildId) {
          this.buildId = catalog.buildId;
        }
      }
    } catch {
      // The runtime remains usable if catalog metadata is temporarily offline.
    }
    if (this.stopped) return;
    this.postSelection();
    this.postParamSchema();
    this.post({
      type: "pfx:runtimeReady",
      protocol: PROTOCOL_VERSION,
      buildId: this.buildId,
      target: this.target,
      scope: this.scope,
    });
    this.postState(null);
  }

  read(): AudioFeatures {
    const now = performance.now();
    const elapsed = Math.max(0, (now - this.beatUpdatedAt) / 1000);
    this.features.beat *= Math.exp(-8 * elapsed);
    this.beatUpdatedAt = now;
    this.features.t = now;
    return this.features;
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener("message", this.onMessage);
    this.renderer.onCameraChange = null;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.websocket?.close();
    this.websocket = null;
  }

  private updateFeatures(input: unknown): void {
    if (!input || typeof input !== "object") return;
    const data = input as Record<string, unknown>;
    const dedicatedBeat = data.type === "beat";
    const inputBeat = Boolean(data.beat);
    const isBeat = dedicatedBeat || (inputBeat && !this.inputBeatActive);
    this.inputBeatActive = inputBeat;
    const bass = clamp01(data.bass ?? this.features.bass);
    const mid = clamp01(data.mid ?? this.features.mid);
    const treble = clamp01(data.treble ?? this.features.treble);
    const explicitLevel = Number(data.level);
    const level = Number.isFinite(explicitLevel)
      ? clamp01(explicitLevel)
      : Math.max(bass, mid, treble);
    let beatCount = Number(data.beatCount ?? data.beat_count ?? this.features.beatCount);
    if (!Number.isFinite(beatCount)) beatCount = this.features.beatCount;
    if (isBeat && beatCount <= this.features.beatCount) beatCount = this.features.beatCount + 1;

    this.features = {
      bass,
      mid,
      treble,
      level,
      bpm: Math.max(0, Number(data.bpm) || this.features.bpm),
      beat: isBeat ? 1 : this.features.beat,
      beatCount,
      t: performance.now(),
      spectrum: optionalFloatArray(data.spectrum) ?? this.features.spectrum,
      waveform: optionalFloatArray(data.waveform) ?? this.features.waveform,
    };
    if (isBeat) this.beatUpdatedAt = performance.now();
  }

  private handleParentMessage(event: MessageEvent): void {
    if (event.source !== window.parent && event.source !== window.opener) return;
    if (this.parentOrigin && event.origin !== this.parentOrigin) return;
    const data = event.data as RemoteRuntimeMessage;
    if (!data || typeof data !== "object") return;
    if (this.nonce && data.nonce !== this.nonce) return;

    if (data.type === "pfx:audio") {
      this.updateFeatures(data.features);
      return;
    }
    if (data.type !== "pfx:command" || typeof data.command !== "string") return;

    if (data.command === "requestParams") {
      this.postParamSchema();
    } else if (data.command === "requestSelection") {
      this.postSelection();
    } else if (data.command === "selection") {
      const id = (data.value as { id?: unknown } | null)?.id;
      if (typeof id === "string") this.applySelection(id, data.seq ?? null);
    } else if (data.command === "scene.select") {
      const value = data.value as { sceneId?: unknown; variantId?: unknown; seq?: unknown } | null;
      const id = typeof value?.variantId === "string"
        ? value.variantId
        : typeof value?.sceneId === "string" ? value.sceneId : null;
      if (id) this.applySelection(id, Number(value?.seq ?? data.seq) || null);
    } else if (data.command === "quality.set") {
      const quality = typeof data.value === "string"
        ? data.value
        : (data.value as { quality?: unknown } | null)?.quality;
      if (typeof quality === "string" && QUALITY_TIERS.has(quality as QualityTier)) {
        this.renderer.params.qualityTier = quality as QualityTier;
        this.postState(data.seq ?? null);
      }
    } else if (data.command === "blackout") {
      this.setBlackout(Boolean(data.value));
      this.postState(data.seq ?? null);
    } else if (data.command === "freeze") {
      this.setFrozen(Boolean(data.value));
      this.postState(data.seq ?? null);
    } else if (data.command === "output.set") {
      this.setOutput(data.value);
      this.postState(data.seq ?? null);
    } else if (data.command === "camera.set") {
      this.setCamera(data.value);
      this.postState(data.seq ?? null);
    } else if (data.command === "camera.mode") {
      const mode = typeof data.value === "string"
        ? data.value
        : (data.value as { mode?: unknown } | null)?.mode;
      if (mode === "auto" || mode === "free" || mode === "fly") {
        this.renderer.setCameraControlMode(mode);
        this.postState(data.seq ?? null);
      }
    } else if (data.command === "camera.frame") {
      this.setCamera(data.value);
      this.postState(data.seq ?? null);
    } else if (data.command === "camera.auto" && data.value === "next") {
      this.renderer.advanceAutoCameraShot();
      this.postState(data.seq ?? null);
    } else if (data.command === "camera.fly" && data.value === "restart") {
      this.renderer.restartFlyCamera();
      this.postState(data.seq ?? null);
    } else if (data.command === "visual.set") {
      const value = data.value as { key?: unknown; value?: unknown } | null;
      if (typeof value?.key === "string") {
        this.setVisualParam(value.key, value.value, data.seq ?? null);
      }
    } else if (data.command === "param") {
      const value = data.value as { id?: unknown; value?: unknown } | null;
      if (typeof value?.id === "string") {
        this.setSceneParam(value.id, value.value, data.seq ?? null);
      }
    } else if (data.command === "scene.reset-params") {
      const selectionId = typeof data.value === "string"
        ? data.value
        : (data.value as { selectionId?: unknown } | null)?.selectionId;
      this.resetSceneParams(typeof selectionId === "string" ? selectionId : null, data.seq ?? null);
    } else if (data.command === "visual.reset-overrides") {
      this.resetVisualOverrides(data.seq ?? null);
    } else if (data.command === "state.request") {
      this.postState(data.seq ?? null);
    }
  }

  private applySelection(id: string, seq: number | null): boolean {
    const selection = selectionForId(id);
    if (
      !selection ||
      !sceneIsAvailableInRemoteScope(selection.scene, this.scope) ||
      !sceneIsAvailableForRemoteTarget(selection.scene, this.target)
    ) {
      this.post({
        type: "pfx:sceneApplied",
        status: "error",
        error: "unknown-scene",
        selectionId: id,
        seq,
        buildId: this.buildId,
      });
      return false;
    }
    applyScenePreset(this.renderer, selection.scene, selection.variant);
    this.current = selection;
    this.applyVisualOverrides();
    this.applySceneOverrides();
    this.post({
      type: "pfx:sceneApplied",
      status: "ok",
      sceneId: getSceneId(selection.scene),
      variantId: selection.variant ? getVariantId(selection.scene, selection.variant) : null,
      selectionId: selection.selectionId,
      sceneType: selection.scene.sceneType ?? "bioclast",
      seq,
      buildId: this.buildId,
    });
    this.post({
      type: "pfx:padSelectionChanged",
      id: "production-scenes",
      currentId: selection.selectionId,
    });
    this.postParamSchema();
    this.postState(seq);
    return true;
  }

  private postSelection(): void {
    this.post({
      type: "pfx:padSelection",
      id: "production-scenes",
      items: selectionItems(this.scope, this.target),
      currentId: this.current?.selectionId ?? null,
    });
  }

  private paramSchema(): RemoteParamSchema[] {
    if (!this.current) return [];
    const context = {
      sceneName: this.current.scene.name,
      sceneType: this.current.scene.sceneType ?? "bioclast",
      profile: this.current.scene.controlProfile,
      values: this.current.scene.values,
    };
    const params = this.renderer.params as unknown as Record<string, number | boolean>;
    return filterBySceneProfile(context, "advanced").flatMap((section) => (
      section.controls.flatMap((control): RemoteParamSchema[] => {
        const key = String(control.key);
        if (!(key in VISUAL_PARAM_SPECS)) return [];
        const current = params[key];
        if (control.kind === "toggle" && typeof current === "boolean") {
          return [{
            id: key,
            label: `${section.title} / ${control.label}`,
            default: current,
            type: "toggle",
          }];
        }
        if (control.kind !== "slider" || typeof current !== "number") return [];
        return [{
          id: key,
          label: `${section.title} / ${control.label}`,
          min: control.min,
          max: control.max,
          default: current,
          step: control.step,
          type: "float",
        }];
      })
    ));
  }

  private postParamSchema(): void {
    this.post({
      type: "pfx:padReady",
      id: "production-scenes",
      params: this.paramSchema(),
    });
  }

  private postState(seq: number | null): void {
    this.post({
      type: "pfx:runtimeState",
      protocol: PROTOCOL_VERSION,
      buildId: this.buildId,
      sceneId: this.current ? getSceneId(this.current.scene) : null,
      variantId: this.current?.variant ? getVariantId(this.current.scene, this.current.variant) : null,
      selectionId: this.current?.selectionId ?? null,
      sceneType: this.renderer.sceneType,
      scope: this.scope,
      quality: this.renderer.params.qualityTier,
      frameMs: this.renderer.frameMs,
      renderer: this.renderer.getDiagnostics(),
      blackout: this.canvas.style.opacity === "0",
      frozen: this.frozen,
      output: { ...this.output },
      camera: this.renderer.getCameraState(),
      visual: Object.fromEntries(
        Object.keys(VISUAL_PARAM_SPECS).map((key) => [
          key,
          this.renderer.params[key as VisualParamKey],
        ]),
      ),
      audio: {
        bass: this.features.bass,
        mid: this.features.mid,
        treble: this.features.treble,
        level: this.features.level,
        bpm: this.features.bpm,
        beat: this.features.beat,
        beatCount: this.features.beatCount,
        spectrumBins: this.features.spectrum?.length ?? 0,
        waveformSamples: this.features.waveform?.length ?? 0,
      },
      seq,
    });
  }

  private setBlackout(enabled: boolean): void {
    this.canvas.style.opacity = enabled ? "0" : "1";
  }

  private setFrozen(enabled: boolean): void {
    if (enabled === this.frozen) return;
    this.frozen = enabled;
    if (enabled) this.renderer.stop();
    else this.renderer.start();
  }

  private setOutput(input: unknown): void {
    if (!input || typeof input !== "object") return;
    const value = input as Record<string, unknown>;
    const brightness = optionalNumber(value.brightness);
    const width = optionalNumber(value.width);
    const height = optionalNumber(value.height);
    const detail = String(value.detail ?? this.output.detail).toLowerCase();
    const projection = String(value.projection ?? this.output.projection).toLowerCase();
    const warp = optionalNumber(value.warp);
    if (brightness !== null) {
      this.output.brightness = Math.max(0.5, Math.min(2, brightness));
    }
    if (width !== null) {
      this.output.width = Math.max(64, Math.min(2048, Math.round(width)));
    }
    if (height !== null) {
      this.output.height = Math.max(64, Math.min(2048, Math.round(height)));
    }
    if (detail === "auto" || detail === "light" || detail === "standard" || detail === "dense") {
      this.output.detail = detail;
    }
    if (OUTPUT_PROJECTIONS.has(projection as OutputProjection)) {
      this.output.projection = projection as OutputProjection;
    }
    if (warp !== null) {
      this.output.warp = Math.max(0, Math.min(1, warp));
      // Senders that predate the Fov control only carry warp. Mirroring warp
      // into fov reproduces the old coupled behaviour for those senders; an
      // explicit fov below overrides it.
      this.output.fov = this.output.warp;
    }
    const fov = optionalNumber(value.fov);
    if (fov !== null) {
      this.output.fov = Math.max(0, Math.min(1, fov));
    }
    if (value.calibration !== undefined && value.calibration !== null) {
      const raw = String(value.calibration).toLowerCase();
      this.output.calibration = raw === "1" || raw === "true" || raw === "on";
    }
    const tileColumns = optionalNumber(value.tileColumns);
    const tileRows = optionalNumber(value.tileRows);
    const tileX = optionalNumber(value.tileX);
    const tileY = optionalNumber(value.tileY);
    const hasTileInput = [value.tileColumns, value.tileRows, value.tileX, value.tileY]
      .some((input) => input !== undefined && input !== null && input !== "");
    if (hasTileInput && tileColumns !== null) {
      this.output.tileColumns = Math.max(1, Math.min(8, Math.round(tileColumns)));
    }
    if (hasTileInput && tileRows !== null) {
      this.output.tileRows = Math.max(1, Math.min(8, Math.round(tileRows)));
    }
    if (hasTileInput && tileX !== null) {
      this.output.tileX = Math.max(0, Math.min(this.output.tileColumns - 1, Math.round(tileX)));
    }
    if (hasTileInput && tileY !== null) {
      this.output.tileY = Math.max(0, Math.min(this.output.tileRows - 1, Math.round(tileY)));
    }
    if (hasTileInput) {
      this.output.viewX = this.output.tileX / this.output.tileColumns;
      this.output.viewY = this.output.tileY / this.output.tileRows;
      this.output.viewWidth = 1 / this.output.tileColumns;
      this.output.viewHeight = 1 / this.output.tileRows;
    }
    const hasViewInput = [value.viewX, value.viewY, value.viewWidth, value.viewHeight]
      .some((input) => input !== undefined && input !== null && input !== "");
    if (hasViewInput) {
      const viewWidth = optionalNumber(value.viewWidth);
      const viewHeight = optionalNumber(value.viewHeight);
      const safeWidth = viewWidth !== null
        ? Math.max(0.01, Math.min(1, viewWidth))
        : this.output.viewWidth;
      const safeHeight = viewHeight !== null
        ? Math.max(0.01, Math.min(1, viewHeight))
        : this.output.viewHeight;
      const viewX = optionalNumber(value.viewX);
      const viewY = optionalNumber(value.viewY);
      this.output.viewWidth = safeWidth;
      this.output.viewHeight = safeHeight;
      if (viewX !== null) this.output.viewX = Math.max(0, Math.min(1 - safeWidth, viewX));
      if (viewY !== null) this.output.viewY = Math.max(0, Math.min(1 - safeHeight, viewY));
    }
    const fill = optionalNumber(value.fill);
    if (fill !== null) {
      this.output.fill = Math.max(1, Math.min(3.5, fill));
    }
    const fillY = optionalNumber(value.fillY);
    if (fillY !== null) {
      this.output.fillY = Math.max(1, Math.min(3.5, fillY));
    }
    this.renderer.setOutputProjection(this.output.projection, this.output.warp, this.output.fov);
    this.renderer.setOutputBrightness(this.output.brightness);
    this.renderer.setOutputCalibration(this.output.calibration);
    this.renderer.setOutputView(
      this.output.viewX,
      this.output.viewY,
      this.output.viewWidth,
      this.output.viewHeight,
    );
    this.renderer.setOutputFill(this.output.fill);
    this.renderer.setOutputFillY(this.output.fillY);
    if (this.target === "sl-moap" || this.output.detail !== "auto") {
      this.renderer.params.qualityTier = this.wallQualityTier();
    }
    document.body.style.removeProperty("filter");
  }

  private wallQualityTier(): QualityTier {
    if (this.output.detail === "light") return "mobile";
    if (this.output.detail === "standard") return "standard";
    if (this.output.detail === "dense") return "high";
    if (this.output.width >= 2048 || this.output.height >= 1024) return "high";
    if (this.output.width >= 1024 || this.output.height >= 512) return "standard";
    return "mobile";
  }

  private setCamera(input: unknown): void {
    if (!input || typeof input !== "object") return;
    this.renderer.setCameraState(input as Partial<CameraSyncState>);
  }

  private setCameraMode(input: unknown): boolean {
    const mode = String(input ?? "") as CameraControlMode;
    if (mode !== "auto" && mode !== "free" && mode !== "fly") return false;
    this.renderer.setCameraControlMode(mode);
    return true;
  }

  private setVisualParam(key: string, input: unknown, seq: number | null): boolean {
    if (!(key in VISUAL_PARAM_SPECS)) {
      this.post({ type: "pfx:visualParamChanged", status: "error", key, seq });
      return false;
    }
    const typedKey = key as VisualParamKey;
    const spec = VISUAL_PARAM_SPECS[typedKey];
    const numeric = Number(input);
    if (!Number.isFinite(numeric)) {
      this.post({ type: "pfx:visualParamChanged", status: "error", key, seq });
      return false;
    }
    const clamped = Math.max(spec.min, Math.min(spec.max, numeric));
    const value = "boolean" in spec && spec.boolean ? clamped >= 0.5 : clamped;
    (this.renderer.params as unknown as Record<string, number | boolean>)[typedKey] = value;
    this.visualOverrides.set(typedKey, value);
    if (typedKey.startsWith("camera") || typedKey.startsWith("camAuto")) {
      this.renderer.clearAltCameraOverride();
    }
    this.post({
      type: "pfx:visualParamChanged",
      status: "ok",
      key: typedKey,
      value,
      seq,
    });
    return true;
  }

  private applyVisualOverrides(): void {
    for (const [key, value] of this.visualOverrides) {
      (this.renderer.params as unknown as Record<string, number | boolean>)[key] = value;
    }
  }

  private setSceneParam(
    key: string,
    input: unknown,
    seq: number | null,
    selectionId: string | null = this.current?.selectionId ?? null,
  ): boolean {
    if (!selectionId || this.current?.selectionId !== selectionId || !(key in VISUAL_PARAM_SPECS)) {
      this.post({ type: "pfx:visualParamChanged", status: "error", key, seq });
      return false;
    }
    const typedKey = key as VisualParamKey;
    const spec = VISUAL_PARAM_SPECS[typedKey];
    const numeric = Number(input);
    if (!Number.isFinite(numeric)) {
      this.post({ type: "pfx:visualParamChanged", status: "error", key, seq });
      return false;
    }
    const clamped = Math.max(spec.min, Math.min(spec.max, numeric));
    const value = "boolean" in spec && spec.boolean ? clamped >= 0.5 : clamped;
    let overrides = this.sceneOverrides.get(selectionId);
    if (!overrides) {
      overrides = new Map<VisualParamKey, number | boolean>();
      this.sceneOverrides.set(selectionId, overrides);
    }
    overrides.set(typedKey, value);
    (this.renderer.params as unknown as Record<string, number | boolean>)[typedKey] = value;
    this.post({
      type: "pfx:visualParamChanged",
      status: "ok",
      key: typedKey,
      value,
      selectionId,
      seq,
    });
    return true;
  }

  private applySceneOverrides(): void {
    if (!this.current) return;
    const overrides = this.sceneOverrides.get(this.current.selectionId);
    if (!overrides) return;
    for (const [key, value] of overrides) {
      (this.renderer.params as unknown as Record<string, number | boolean>)[key] = value;
    }
  }

  private resetSceneParams(selectionId: string | null, seq: number | null): void {
    if (!selectionId || this.current?.selectionId !== selectionId) return;
    this.sceneOverrides.delete(selectionId);
    applyScenePreset(this.renderer, this.current.scene, this.current.variant);
    this.applyVisualOverrides();
    this.postParamSchema();
    this.postState(seq);
  }

  private resetVisualOverrides(seq: number | null): void {
    this.visualOverrides.clear();
    if (this.current) {
      applyScenePreset(this.renderer, this.current.scene, this.current.variant);
      this.applySceneOverrides();
    }
    this.postState(seq);
  }

  private post(message: Record<string, unknown>): void {
    const target = window.parent !== window ? window.parent : window.opener;
    if (!target) return;
    target.postMessage(
      { ...message, nonce: this.nonce ?? undefined },
      this.parentOrigin ?? "*",
    );
  }

  private connectWebsocket(host: string): void {
    const url = websocketUrl(host);
    if (!url) return;
    try {
      this.websocket = new WebSocket(url);
    } catch {
      this.scheduleReconnect(host);
      return;
    }
    this.websocket.addEventListener("message", (event) => {
      try {
        const data = JSON.parse(String(event.data)) as Record<string, unknown>;
        if (data.type === "visual_command" && typeof data.command === "string") {
          this.handleVisualCommand(data.command, Number(data.seq) || null);
        } else {
          this.updateFeatures(data);
        }
      } catch {
        // Ignore malformed fanout frames; the next valid frame restores state.
      }
    });
    this.websocket.addEventListener("close", () => this.scheduleReconnect(host));
    this.websocket.addEventListener("error", () => this.websocket?.close());
  }

  private scheduleReconnect(host: string): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connectWebsocket(host);
    }, 2000);
  }

  private handleVisualCommand(command: string, seq: number | null): void {
    const parts = command.trim().split(/\s+/);
    if (parts[0] === "visual" && parts[1] === "scene" && parts[2]) {
      this.applySelection(parts[3] || parts[2], seq);
    } else if (parts[0] === "scene.select" && parts[1]) {
      this.applySelection(parts[2] || parts[1], seq);
    } else if (parts[0] === "blackout") {
      this.setBlackout(parts[1] !== "off");
    } else if (parts[0] === "freeze") {
      this.setFrozen(parts[1] !== "off");
    } else if (parts[0] === "output.set") {
      this.setOutput({
        brightness: parts[1],
        width: parts[2],
        height: parts[3],
        projection: parts[4],
        warp: parts[5],
        detail: parts[6],
        // Trailing fields are optional so older senders keep working.
        fov: parts[7],
        calibration: parts[8],
        fill: parts[9],
        fillY: parts[10],
      });
      this.postState(seq);
    } else if (parts[0] === "camera.set") {
      this.setCamera({ orbit: parts[1], tilt: parts[2], zoom: parts[3] });
      this.postState(seq);
    } else if (parts[0] === "camera.mode") {
      if (this.setCameraMode(parts[1])) this.postState(seq);
    } else if (parts[0] === "camera.frame") {
      this.setCamera({
        mode: "free",
        position: [parts[1], parts[2], parts[3]].map(Number),
        target: [parts[4], parts[5], parts[6]].map(Number),
        fov: Number(parts[7]),
      });
      this.postState(seq);
    } else if (parts[0] === "camera.auto" && parts[1] === "next") {
      this.renderer.advanceAutoCameraShot();
      this.postState(seq);
    } else if (parts[0] === "camera.fly" && parts[1] === "restart") {
      this.renderer.restartFlyCamera();
      this.postState(seq);
    } else if (parts[0] === "visual.set") {
      this.setVisualParam(parts[1] ?? "", parts[2], seq);
    } else if (parts[0] === "visual" && parts[1] === "set") {
      this.setVisualParam(parts[2] ?? "", parts[3], seq);
    } else if (parts[0] === "visual.reset-overrides") {
      this.resetVisualOverrides(seq);
    } else if (parts[0] === "scene.param" && parts[1]) {
      this.setSceneParam(parts[2] ?? "", parts[3], seq, parts[1]);
    } else if (parts[0] === "scene.reset-params" && parts[1]) {
      this.resetSceneParams(parts[1], seq);
    } else if (parts[0] === "state.request") {
      this.postState(seq);
    }
  }
}

export function isRemoteRuntime(params: URLSearchParams): boolean {
  return params.get("remote") === "1";
}
